import { useClient } from 'src/api/axios/config';
import { FanguardApiContractsClientAssetModel } from 'src/api/generated';
import { useUpdateClientAssets } from 'src/hooks/api/useClientsEndpoint';
import { getContentTypeFromDataUrl, stripDataUrlPrefix } from 'src/pages/ClientConfiguration/Assets/utils';
import { FileData } from 'src/pages/Devices/GroupDetails/TabPages/GroupDesign/components/FileUpload/FileUpload';
import { sanitizeFileName } from 'src/utils/sanitize';

import { clientsQueries } from '@/api/queryKeys';
import { useQueryClient } from '@tanstack/react-query';

interface UploadToLibraryOptions {
  // Fields that store an asset reference need the generated id, which costs an extra list read.
  resolveAsset?: boolean;
}

export const useAssetLibraryUpload = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useUpdateClientAssets();

  const uploadFileToLibrary = async (
    fileData: FileData,
    { resolveAsset = false }: UploadToLibraryOptions = {},
  ): Promise<FanguardApiContractsClientAssetModel | undefined> => {
    const dataUrl = fileData.image || fileData.url || '';
    const name = sanitizeFileName(fileData.name || '');

    const created = (await mutateAsync({
      name,
      file: stripDataUrlPrefix(dataUrl),
      contentType: getContentTypeFromDataUrl(dataUrl),
    } as FanguardApiContractsClientAssetModel)) as FanguardApiContractsClientAssetModel | undefined;

    if (!resolveAsset || created?.id) return created;

    // The upload response is built from S3 metadata and omits the generated id, so it has to be
    // read back from the refreshed library.
    const assets = await queryClient.fetchQuery(clientsQueries.assets(client));

    return assets?.find((asset) => sanitizeFileName(asset.name || '') === name);
  };

  return { uploadFileToLibrary, isUploading: isPending };
};
